import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  Skeleton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { RxTable } from './RxTable';
import { RxTableGrouped } from './RxTableGrouped';
import { RxImageModal } from './RxImageModal';
import RxImagePanel, { RxImagePanelMedication } from '../../common/RxImagePanel';
import { rxService, RxPrescription, RxImage } from '../../../services/rxService';
import { usePharmacyStore } from '../../../stores/pharmacyStore';

interface RxImageViewerProps {
  patientId: string;
  initialFilter?: 'active' | 'inactive' | 'all';
}

export const RxImageViewer: React.FC<RxImageViewerProps> = ({ patientId, initialFilter = 'active' }) => {
  const { enqueueSnackbar } = useSnackbar();
  const activePharmacy = usePharmacyStore((state) => state.activePharmacy);

  // State for prescriptions list
  const [prescriptions, setPrescriptions] = useState<RxPrescription[]>([]);
  const [loadingPrescriptions, setLoadingPrescriptions] = useState(false);
  const [prescriptionsError, setPrescriptionsError] = useState<string | null>(null);
  const [prescriptionFilter, setPrescriptionFilter] = useState<'active' | 'inactive' | 'all'>(initialFilter);

  // State for image viewing
  const [selectedPrescription, setSelectedPrescription] = useState<RxPrescription | null>(null);
  const [selectedPrescriptions, setSelectedPrescriptions] = useState<RxPrescription[]>([]);
  const [selectedImage, setSelectedImage] = useState<RxImage | null>(null);
  const [selectedImages, setSelectedImages] = useState<RxImage[]>([]);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 });

  // Shared RxImagePanel viewer (same one used in Clinical Verification) — opened
  // for linked Rx numbers. It self-fetches every attachment (Script Image + Hard
  // Copy) for the Rx#, with its own tabs, zoom/rotate/flip, and PDF support.
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelRxNumber, setPanelRxNumber] = useState<string | null>(null);
  const [panelStatus, setPanelStatus] = useState<number | undefined>(undefined);
  const [panelAdjState, setPanelAdjState] = useState<number | null | undefined>(undefined);

  const openPanel = useCallback((rxNumber: string, status?: number, rxAdjState?: number | null) => {
    setPanelRxNumber(rxNumber);
    setPanelStatus(status);
    setPanelAdjState(rxAdjState);
    setPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    setPanelRxNumber(null);
  }, []);

  const panelMedications = useMemo<RxImagePanelMedication[]>(
    () => (panelRxNumber ? [{ rxNumber: panelRxNumber, status: panelStatus, rxAdjState: panelAdjState }] : []),
    [panelRxNumber, panelStatus, panelAdjState],
  );

  // Parse patient ID to get components
  const parsePatientId = useCallback(() => {
    if (!patientId) return { pharmacyId: null, patientNumber: null };

    if (patientId.includes('_')) {
      const [pharmacyId, patientNumber] = patientId.split('_');
      return { pharmacyId, patientNumber };
    }

    return {
      pharmacyId: activePharmacy?.id || null,
      patientNumber: patientId,
    };
  }, [patientId, activePharmacy]);

  // Fetch prescriptions list
  const fetchPrescriptions = useCallback(async () => {
    const { pharmacyId, patientNumber } = parsePatientId();

    if (!pharmacyId || !patientNumber) {
      setPrescriptionsError('Invalid patient ID or missing pharmacy context');
      return;
    }

    const startTime = performance.now();
    console.group('📷 RX IMAGE VIEWER - LOAD PRESCRIPTIONS');
    console.log('⏰ Load initiated at:', new Date().toISOString());
    console.log('🏥 Pharmacy ID:', pharmacyId);
    console.log('👤 Patient ID:', patientId);
    console.log('👤 Patient Number:', patientNumber);
    console.log('🔍 Filter:', prescriptionFilter);
    console.log('📡 API Call: rxService.getRxList(', pharmacyId, ',', patientId, ', 100,', prescriptionFilter, ')');

    setLoadingPrescriptions(true);
    setPrescriptionsError(null);

    try {
      const data = await rxService.getRxList(pharmacyId, patientId, 100, prescriptionFilter);

      const endTime = performance.now();
      const loadTime = (endTime - startTime).toFixed(2);

      console.log('✅ Prescriptions loaded successfully');
      console.log('📊 Found', data?.length || 0, 'prescriptions');
      console.log('⏱️  Load time:', loadTime, 'ms');
      console.groupEnd();

      setPrescriptions(data);
    } catch (error: any) {
      const endTime = performance.now();
      const loadTime = (endTime - startTime).toFixed(2);

      console.error('❌ Failed to fetch prescriptions');
      console.error('⏱️  Failed after:', loadTime, 'ms');
      console.error('Error:', error);
      console.groupEnd();

      setPrescriptionsError(error.message || 'Failed to load prescriptions');
      enqueueSnackbar('Failed to load prescriptions', { variant: 'error' });
    } finally {
      setLoadingPrescriptions(false);
    }
  }, [patientId, parsePatientId, enqueueSnackbar, prescriptionFilter]);

  // Fetch specific image
  const fetchImage = useCallback(async (prescription: RxPrescription) => {
    const { pharmacyId, patientNumber } = parsePatientId();

    if (!pharmacyId || !patientNumber) {
      setImageError('Invalid patient ID or missing pharmacy context');
      return;
    }

    if (!prescription.scriptImageContentId) {
      setImageError('No image ID available for this prescription');
      return;
    }

    setLoadingImage(true);
    setImageError(null);

    try {
      const imageData = await rxService.getRxImage(
        pharmacyId,
        patientId,
        prescription.scriptImageContentId
      );

      if (!imageData) {
        throw new Error('No image data returned');
      }

      setSelectedImage(imageData);
    } catch (error: any) {
      console.error('Failed to fetch image:', error);
      setImageError(error.message || 'Failed to load image');
      enqueueSnackbar('Failed to load image', { variant: 'error' });
    } finally {
      setLoadingImage(false);
    }
  }, [patientId, parsePatientId, enqueueSnackbar]);

  // Handle view image
  const handleViewImage = useCallback(async (prescription: RxPrescription) => {
    if (!prescription.hasImage) {
      enqueueSnackbar('No image available for this prescription', { variant: 'info' });
      return;
    }

    // Linked Rx → open the shared RxImagePanel viewer (it pulls every attachment
    // for the Rx# itself, so a single "view" already shows Script + Hard Copy).
    if (prescription.rxNumber) {
      openPanel(prescription.rxNumber, prescription.status, prescription.rxAdjState);
      return;
    }

    // Unlinked scanned document (no Rx#) → the panel can't fetch by Rx#, so fall
    // back to the direct image modal keyed by scriptImageContentId.
    setSelectedPrescription(prescription);
    setSelectedPrescriptions([prescription]);
    setSelectedImages([]);
    setImageModalOpen(true);
    await fetchImage(prescription);
  }, [fetchImage, enqueueSnackbar, openPanel]);

  // Handle view all images for multi-document RX
  const handleViewAllImages = useCallback(async (prescriptions: RxPrescription[]) => {
    const validPrescriptions = prescriptions.filter(p => p.hasImage);

    if (validPrescriptions.length === 0) {
      enqueueSnackbar('No images available for these prescriptions', { variant: 'info' });
      return;
    }

    // Linked Rx → the RxImagePanel already lists every attachment (Script + Hard
    // Copy) for the Rx# in its own tabs, so route there instead of the modal.
    const linkedRxNumber = validPrescriptions[0].rxNumber;
    if (linkedRxNumber) {
      openPanel(linkedRxNumber, validPrescriptions[0].status, validPrescriptions[0].rxAdjState);
      return;
    }

    // Sort prescriptions: Script Images (type 1) first, then Hard Copies (type 2)
    const sortedPrescriptions = [...validPrescriptions].sort((a, b) => {
      // Script Images (type 1) come first
      if (a.scriptImageType === 1 && b.scriptImageType !== 1) return -1;
      if (b.scriptImageType === 1 && a.scriptImageType !== 1) return 1;

      // Within same type, sort by scan date (newest first)
      const dateA = a.scanDate ? new Date(a.scanDate).getTime() : 0;
      const dateB = b.scanDate ? new Date(b.scanDate).getTime() : 0;
      return dateB - dateA;
    });

    setSelectedPrescriptions(sortedPrescriptions);
    setSelectedPrescription(sortedPrescriptions[0]);
    setSelectedImages([]);
    setLoadingProgress({ current: 0, total: sortedPrescriptions.length });
    setImageModalOpen(true);
    setLoadingImage(true);
    setImageError(null);

    const { pharmacyId, patientNumber } = parsePatientId();
    if (!pharmacyId || !patientNumber) {
      setImageError('Invalid patient ID or missing pharmacy context');
      setLoadingImage(false);
      return;
    }

    try {
      const images: RxImage[] = [];

      for (let i = 0; i < sortedPrescriptions.length; i++) {
        const prescription = sortedPrescriptions[i];
        setLoadingProgress({ current: i + 1, total: sortedPrescriptions.length });

        if (prescription.scriptImageContentId) {
          try {
            const imageData = await rxService.getRxImage(
              pharmacyId,
              patientId,
              prescription.scriptImageContentId
            );
            if (imageData) {
              images.push(imageData);
            }
          } catch (error) {
            console.error(`Failed to fetch image ${prescription.scriptImageContentId}:`, error);
            // Continue loading other images even if one fails
          }
        }
      }

      if (images.length > 0) {
        setSelectedImages(images);
        setSelectedImage(images[0]);
      } else {
        throw new Error('No images could be loaded');
      }
    } catch (error: any) {
      console.error('Failed to fetch images:', error);
      setImageError(error.message || 'Failed to load images');
      enqueueSnackbar('Failed to load images', { variant: 'error' });
    } finally {
      setLoadingImage(false);
      setLoadingProgress({ current: 0, total: 0 });
    }
  }, [patientId, parsePatientId, enqueueSnackbar, openPanel]);

  // Handle download image
  const handleDownloadImage = useCallback(async (prescription: RxPrescription) => {
    if (!prescription.hasImage) {
      enqueueSnackbar('No image available for this prescription', { variant: 'info' });
      return;
    }

    const { pharmacyId, patientNumber } = parsePatientId();

    if (!pharmacyId || !patientNumber) {
      enqueueSnackbar('Invalid patient ID or missing pharmacy context', { variant: 'error' });
      return;
    }

    try {
      // Fetch the image first if not already loaded
      let image = selectedImage;
      if (!image || selectedImage?.scriptImageContentId !== prescription.scriptImageContentId) {
        const imageData = await rxService.getRxImage(
          pharmacyId,
          patientId,
          prescription.scriptImageContentId
        );
        image = imageData;
      }

      if (image) {
        rxService.downloadRxImage(image, prescription.rxNumber || 'Unknown');
        enqueueSnackbar('Image downloaded successfully', { variant: 'success' });
      }
    } catch (error: any) {
      console.error('Failed to download image:', error);
      enqueueSnackbar('Failed to download image', { variant: 'error' });
    }
  }, [patientId, selectedImage, parsePatientId, enqueueSnackbar]);

  // Handle modal download
  const handleModalDownload = useCallback(() => {
    if (selectedImage && selectedPrescription) {
      rxService.downloadRxImage(selectedImage, selectedPrescription.rxNumber || 'Unknown');
      enqueueSnackbar('Image downloaded successfully', { variant: 'success' });
    }
  }, [selectedImage, selectedPrescription, enqueueSnackbar]);

  // Handle modal close
  const handleCloseModal = useCallback(() => {
    setImageModalOpen(false);
    setSelectedPrescription(null);
    setSelectedPrescriptions([]);
    setSelectedImage(null);
    setSelectedImages([]);
    setImageError(null);
    setLoadingProgress({ current: 0, total: 0 });
  }, []);

  // Fetch prescriptions on mount
  useEffect(() => {
    if (patientId) {
      fetchPrescriptions();
    }
  }, [patientId, fetchPrescriptions]);

  // Calculate status summary - must be before any conditional returns
  const statusSummary = React.useMemo(() => {
    const summary = {
      fillable: 0,
      unfilled: 0,
      inProcess: 0,
      inactive: 0,
      total: prescriptions.length,
    };

    prescriptions.forEach((rx) => {
      if (rx.isMistake || rx.inactive) {
        summary.inactive++;
      } else if (rx.status === 3) {
        summary.unfilled++;
      } else if (rx.status === 2 || rx.status === 7) {
        summary.inProcess++;
      } else if (rx.canFill) {
        summary.fillable++;
      }
    });

    return summary;
  }, [prescriptions]);

  // Loading skeleton
  if (loadingPrescriptions && prescriptions.length === 0) {
    return (
      <Box p={2}>
        <Skeleton variant="rectangular" height={60} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={400} />
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2 }}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Prescription Images</Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchPrescriptions}
            disabled={loadingPrescriptions}
          >
            Refresh
          </Button>
        </Box>

        {/* Status Summary */}
        {prescriptions.length > 0 && (
          <Box display="flex" gap={2} mb={2} flexWrap="wrap">
            <Chip
              label={`Total: ${statusSummary.total}`}
              size="small"
              variant="outlined"
            />
            {statusSummary.fillable > 0 && (
              <Chip
                label={`Fillable: ${statusSummary.fillable}`}
                size="small"
                color="success"
                variant="outlined"
              />
            )}
            {statusSummary.unfilled > 0 && (
              <Chip
                label={`Unfilled: ${statusSummary.unfilled}`}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
            {statusSummary.inProcess > 0 && (
              <Chip
                label={`In Process: ${statusSummary.inProcess}`}
                size="small"
                color="warning"
                variant="outlined"
              />
            )}
            {statusSummary.inactive > 0 && (
              <Chip
                label={`Inactive: ${statusSummary.inactive}`}
                size="small"
                color="default"
                variant="outlined"
              />
            )}
          </Box>
        )}

        {/* Error Alert */}
        {prescriptionsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {prescriptionsError}
          </Alert>
        )}

        {/* Prescriptions Table - Using Grouped Table for better handling of multiple images per RX */}
        <RxTableGrouped
          prescriptions={prescriptions}
          loading={loadingPrescriptions}
          error={prescriptionsError}
          onViewImage={handleViewImage}
          onDownloadImage={handleDownloadImage}
          onViewAllImages={handleViewAllImages}
          filter={prescriptionFilter}
          onFilterChange={setPrescriptionFilter}
        />

        {/* Image Viewer Modal — fallback for unlinked scanned documents (no Rx#) */}
        <RxImageModal
          open={imageModalOpen}
          onClose={handleCloseModal}
          prescription={selectedPrescription}
          prescriptions={selectedPrescriptions}
          image={selectedImage}
          images={selectedImages}
          loading={loadingImage}
          loadingProgress={loadingProgress}
          error={imageError}
          onDownload={handleModalDownload}
        />

        {/* Shared RxImagePanel viewer (same as Clinical Verification) — primary
            viewer for linked Rx numbers. */}
        <Dialog
          open={panelOpen}
          onClose={closePanel}
          fullScreen
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
            <Typography variant="h6">
              Prescription Image{panelRxNumber ? ` — Rx #${panelRxNumber}` : ''}
            </Typography>
            <IconButton onClick={closePanel} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ flex: 1, minHeight: 0 }}>
              {panelOpen && (
                <RxImagePanel
                  patientId={patientId}
                  pharmacyId={parsePatientId().pharmacyId || undefined}
                  medications={panelMedications}
                  activeDrugIndex={0}
                  resetKey={panelRxNumber ?? undefined}
                  fitMode="height"
                />
              )}
            </Box>
          </DialogContent>
        </Dialog>
      </Paper>
    </Box>
  );
};